import { ExternalLink, Plus, Trash2, User } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { Callout } from '@/components/patterns/callout';
import { FilterBar, FilterBusca, FilterLimpar, FilterSelect } from '@/components/patterns/filter-bar';
import { TabelaPainel } from '@/components/patterns/data-table';
import { EstadoErro, EstadoVazio } from '@/components/patterns/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, inputClasses } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import { MODULOS_NOVIDADE, TIPOS_NOVIDADE, type Novidade, type NovidadeInput } from '@/data/types';
import { useAsyncAction } from '@/hooks/use-async-action';
import { useResource } from '@/hooks/use-resource';
import { CATEGORIAS, plural, rotuloCategoria, sanitizarVersao, texto } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Quem gerencia novidades (criar E excluir) é a capability
 * `gestao-novidades` — mesmo padrão de `AdministracaoDialog` (`sysadmin`/
 * `sre` sempre passam por bypass no backend, então também valem aqui). Os
 * botões só ESCONDEM pra quem não tem; a garantia de verdade é o
 * `require("gestao-novidades")` nas rotas de escrita. */
const PODE_GERENCIAR_NOVIDADES = ['gestao-novidades', 'sysadmin', 'sre'];

/**
 * Novidades.
 *
 * Esta é a tela que mais se afasta do app original — e de propósito. Lá a
 * lista era um grid de seis colunas, com o resumo espremido numa célula; aqui é
 * um feed agrupado por versão, porque o que o usuário faz nesta tela é *ler* o
 * que mudou antes de decidir atualizar, não comparar linhas numa planilha.
 *
 * Os três filtros do schema original (versão, módulo, categoria) continuam, mas
 * as opções são derivadas do conteúdo carregado, não de um enum fixo que
 * envelhece a cada release.
 */
export function NovidadesPage({ roles = [] }: { roles?: string[] }) {
  const carregar = useCallback((signal: AbortSignal) => dataSource.listNovidades(signal), []);
  const recurso = useResource<Novidade[]>(carregar);
  const podeGerenciar = PODE_GERENCIAR_NOVIDADES.some((role) => roles.includes(role));
  const [criando, setCriando] = useState(false);
  const [aExcluir, setAExcluir] = useState<Novidade | null>(null);
  const exclusao = useAsyncAction({
    sucesso: 'Novidade excluída.',
    contextoDoErro: 'Não foi possível excluir a novidade',
    aoConcluir: () => {
      setAExcluir(null);
      recurso.recarregar();
    },
  });

  const [busca, setBusca] = useState('');
  const [versao, setVersao] = useState<string | null>(null);
  const [modulo, setModulo] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<string | null>(null);

  const novidades = useMemo(() => recurso.dados ?? [], [recurso.dados]);

  const opcoes = useMemo(() => {
    const unico = (valores: string[]) => [...new Set(valores.filter(Boolean))];
    return {
      versoes: unico(novidades.map((item) => item.version))
        .sort((a, b) => b.localeCompare(a, 'pt-BR'))
        .map((valor) => ({ valor, rotulo: valor })),
      modulos: unico(novidades.map((item) => item.module))
        .sort((a, b) => a.localeCompare(b, 'pt-BR'))
        .map((valor) => ({ valor, rotulo: valor })),
      categorias: unico(novidades.map((item) => item.category))
        .sort((a, b) => rotuloCategoria(a).localeCompare(rotuloCategoria(b), 'pt-BR'))
        .map((valor) => ({ valor, rotulo: rotuloCategoria(valor) })),
      // Sugestões pro campo Product Owner do formulário de criação — não um
      // enum fechado (qualquer um pode ser PO), só evita redigitar um nome
      // já usado. Ver `CriarNovidadeDialog`.
      pos: unico(novidades.map((item) => item.PO)).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    };
  }, [novidades]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return novidades.filter((item) => {
      if (versao && item.version !== versao) return false;
      if (modulo && item.module !== modulo) return false;
      if (categoria && item.category !== categoria) return false;
      if (!termo) return true;
      return `${item.title} ${item.summary} ${item.module} ${item.type}`.toLowerCase().includes(termo);
    });
  }, [novidades, busca, versao, modulo, categoria]);

  /** Agrupa por versão, da mais recente para a mais antiga. */
  const porVersao = useMemo(() => {
    const mapa = new Map<string, Novidade[]>();
    filtradas.forEach((item) => {
      const chave = item.version || 'Sem versão informada';
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave)!.push(item);
    });
    return [...mapa.entries()].sort((a, b) => b[0].localeCompare(a[0], 'pt-BR'));
  }, [filtradas]);

  const filtrosAtivos = [busca.trim() !== '', versao !== null, modulo !== null, categoria !== null].filter(Boolean).length;

  return (
    <PageBody>
      <PageHeader
        eyebrow="Base de conhecimento"
        titulo="Novidades"
        descricao="O que mudou em cada versão, por módulo. Cada nota leva ao artigo correspondente na base de conhecimento."
        acao={
          podeGerenciar ? (
            <Button variant="primary" onClick={() => setCriando(true)}>
              <Plus aria-hidden="true" />
              Nova novidade
            </Button>
          ) : undefined
        }
      />

      <FilterBar>
        <FilterBusca
          id="filtro-busca-novidade"
          label="Buscar novidade"
          valor={busca}
          aoMudar={setBusca}
          placeholder="Título, resumo ou módulo"
        />
        <FilterSelect
          id="filtro-versao-novidade"
          label="Versão"
          valor={versao}
          aoMudar={setVersao}
          rotuloTodos="Todas as versões"
          opcoes={opcoes.versoes}
        />
        <FilterSelect
          id="filtro-modulo-novidade"
          label="Módulo"
          valor={modulo}
          aoMudar={setModulo}
          rotuloTodos="Todos os módulos"
          opcoes={opcoes.modulos}
        />
        <FilterSelect
          id="filtro-categoria-novidade"
          label="Categoria"
          valor={categoria}
          aoMudar={setCategoria}
          rotuloTodos="Todas as categorias"
          opcoes={opcoes.categorias}
        />
        <FilterLimpar
          ativo={filtrosAtivos > 0}
          quantidade={filtrosAtivos}
          aoLimpar={() => {
            setBusca('');
            setVersao(null);
            setModulo(null);
            setCategoria(null);
          }}
        />
      </FilterBar>

      {recurso.estado === 'carregando' ? (
        <div className="flex flex-col gap-4" role="status" aria-label="Carregando novidades">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-32 rounded-card" />
          ))}
        </div>
      ) : recurso.estado === 'erro' ? (
        <TabelaPainel>
          <EstadoErro erro={recurso.erro} aoTentarDeNovo={recurso.recarregar} />
        </TabelaPainel>
      ) : porVersao.length === 0 ? (
        <TabelaPainel>
          <EstadoVazio
            titulo={filtrosAtivos > 0 ? 'Nenhuma novidade com esses filtros' : 'Nenhuma novidade publicada'}
            descricao={
              filtrosAtivos > 0
                ? 'Ajuste a busca, a versão, o módulo ou a categoria para ver outras notas.'
                : 'Ainda não há notas de novidades publicadas para as versões do seu escopo.'
            }
          />
        </TabelaPainel>
      ) : (
        porVersao.map(([versaoDaSecao, notas]) => (
          <section key={versaoDaSecao} className="flex flex-col gap-3">
            <div className="flex items-baseline gap-3 border-b border-border pb-2">
              <span className="h-4 w-[3px] shrink-0 self-center rounded-sm bg-primary" aria-hidden="true" />
              <h2 className="font-narrow text-[24px] font-bold tabular-nums leading-7">{versaoDaSecao}</h2>
              <span className="pan-eyebrow">{plural(notas.length, 'nota', 'notas')}</span>
            </div>
            <ul className="flex flex-col gap-3">
              {notas.map((nota) => (
                <li key={nota.id}>
                  <NotaDeNovidade nota={nota} podeExcluir={podeGerenciar} aoExcluir={() => setAExcluir(nota)} />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      {podeGerenciar ? (
        <CriarNovidadeDialog
          aberto={criando}
          aoFechar={() => setCriando(false)}
          aoCriar={() => recurso.recarregar()}
          posExistentes={opcoes.pos}
        />
      ) : null}

      {/* Confirmação nomeada, não `confirm()` genérico — mesmo padrão do
          cancelamento de agendamento em `AgendamentosPage`. */}
      <Dialog open={Boolean(aExcluir)} onOpenChange={(aberto) => !aberto && setAExcluir(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir esta novidade?</DialogTitle>
            <DialogDescription>
              A nota "{texto(aExcluir?.title)}" ({texto(aExcluir?.version)}) some do feed de Novidades pra todo
              mundo. Não tem como desfazer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAExcluir(null)} disabled={exclusao.executando}>
              Manter novidade
            </Button>
            <Button
              variant="danger"
              loading={exclusao.executando}
              onClick={() => {
                const alvo = aExcluir;
                if (alvo) void exclusao.executar(() => dataSource.excluirNovidade(alvo.id));
              }}
            >
              Excluir novidade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageBody>
  );
}

function NotaDeNovidade({
  nota,
  podeExcluir,
  aoExcluir,
}: {
  nota: Novidade;
  podeExcluir: boolean;
  aoExcluir: () => void;
}) {
  const temLink = Boolean(nota.link?.trim());
  const categoria = rotuloCategoria(nota.category);
  // Módulo e categoria são campos distintos no schema, mas nada impede o
  // backend de mandar o mesmo rótulo nos dois. Repetir a mesma palavra em dois
  // badges lado a lado não informa nada — mostra-se uma vez.
  const categoriaRedundante = categoria.toLowerCase() === texto(nota.module).toLowerCase();

  return (
    <Card as="article" className="flex flex-col gap-3 p-5 transition-shadow duration-base ease-standard hover:shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <TipoBadge tipo={nota.type} />
          <Badge tone="info">{texto(nota.module)}</Badge>
          {categoriaRedundante ? null : <Badge tone="neutral">{categoria}</Badge>}
        </div>
        {podeExcluir ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={aoExcluir}
            aria-label={`Excluir a novidade "${nota.title}"`}
            title="Excluir novidade"
          >
            <Trash2 className="size-4 text-danger" aria-hidden="true" />
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <h3 className="text-card-title">{texto(nota.title)}</h3>
        <p className="text-body text-on-surface-muted">{texto(nota.summary)}</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle pt-3">
        <span className="inline-flex items-center gap-1.5 text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">
          <User className="size-3.5" aria-hidden="true" />
          Product Owner: {texto(nota.PO)}
        </span>
        {temLink ? (
          <a
            href={nota.link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-button text-label text-primary underline-offset-4 hover:underline"
          >
            Ler na base de conhecimento
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </a>
        ) : (
          <span className="pan-pending rounded-chip px-2 py-0.5 text-label" title="Esta nota ainda não tem artigo publicado.">
            Artigo ainda não publicado
          </span>
        )}
      </div>
    </Card>
  );
}

function TipoBadge({ tipo }: { tipo: string }) {
  if (tipo === 'Adaptativa legal') return <Badge tone="warning">Adaptativa legal</Badge>;
  if (tipo === 'Nova funcionalidade') return <Badge tone="success">Nova funcionalidade</Badge>;
  if (tipo === 'Correctiva') return <Badge tone="danger">Correção</Badge>;
  if (tipo === 'Evolução técnica') return <Badge tone="primary">Evolução técnica</Badge>;
  return <Badge tone="pending">Tipo sem dado</Badge>;
}

const NOVIDADE_VAZIA: NovidadeInput = {
  version: '',
  category: '',
  module: '',
  title: '',
  summary: '',
  type: TIPOS_NOVIDADE[0],
  PO: '',
  link: '',
  // Nunca exibido em lugar nenhum da tela (nem aqui, nem no card da nota) —
  // sem sentido pedir no formulário um campo que a UI não usa pra nada.
  imageUrl: '',
};

/** Restrita pela capability `gestao-novidades` — só é montada quando
 * `NovidadesPage` já decidiu que a pessoa pode ver o botão. Dialog, não
 * página própria: mesmo molde de `EditarInstalacaoDialog`/
 * `PersonificarInstalacaoDialog` — ação de escrita disparada a partir de
 * uma tela de listagem que já existe. */
function CriarNovidadeDialog({
  aberto,
  aoFechar,
  aoCriar,
  posExistentes,
}: {
  aberto: boolean;
  aoFechar: () => void;
  aoCriar: () => void;
  posExistentes: string[];
}) {
  const [form, setForm] = useState<NovidadeInput>(NOVIDADE_VAZIA);

  function fechar() {
    setForm(NOVIDADE_VAZIA);
    aoFechar();
  }

  const acao = useAsyncAction({
    sucesso: 'Novidade criada.',
    contextoDoErro: 'Não foi possível criar a novidade',
    aoConcluir: () => {
      aoCriar();
      fechar();
    },
  });

  function campo<K extends keyof NovidadeInput>(chave: K) {
    return (valor: NovidadeInput[K]) => setForm((atual) => ({ ...atual, [chave]: valor }));
  }

  const obrigatoriosPreenchidos =
    form.version.trim() !== '' &&
    form.category.trim() !== '' &&
    form.module.trim() !== '' &&
    form.title.trim() !== '' &&
    form.summary.trim() !== '' &&
    form.PO.trim() !== '';

  function criar() {
    if (!obrigatoriosPreenchidos) return;
    void acao.executar(() =>
      dataSource
        .criarNovidade({
          ...form,
          // Defensivo: cobre quem clica "Publicar" sem sair do campo Versão
          // (sem o `onBlur` rodar) — mesma sanitização dos dois jeitos.
          version: sanitizarVersao(form.version),
          category: form.category.trim(),
          module: form.module.trim(),
          title: form.title.trim(),
          summary: form.summary.trim(),
          PO: form.PO.trim(),
          link: form.link.trim(),
        })
        .then(() => undefined),
    );
  }

  return (
    <Dialog open={aberto} onOpenChange={(valor) => !valor && fechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova novidade</DialogTitle>
          <DialogDescription>
            Publica uma nota no feed de Novidades, visível a todo usuário do PAN assim que salva — não há
            rascunho nem revisão antes de publicar.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="nova-novidade-versao"
            label="Versão"
            obrigatorio
            apoio="4 grupos (ex.: 2.2601.122.0) — falta só o último? completamos ao sair do campo."
          >
            {(props) => (
              <Input
                {...props}
                value={form.version}
                onChange={(e) => campo('version')(e.target.value)}
                onBlur={() => campo('version')(sanitizarVersao(form.version))}
                placeholder="2.2601.122.0"
              />
            )}
          </Field>
          {/* Fechado nas 12 categorias de nasajon.com.br/historico-atualizacoes
              — ver `MODULOS_NOVIDADE`. */}
          <Field id="nova-novidade-modulo" label="Módulo" obrigatorio>
            {(props) => (
              <Select value={form.module} onValueChange={campo('module')}>
                <SelectTrigger id={props.id} aria-describedby={props['aria-describedby']}>
                  <SelectValue placeholder="Selecione o módulo" />
                </SelectTrigger>
                <SelectContent>
                  {MODULOS_NOVIDADE.map((valor) => (
                    <SelectItem key={valor} value={valor}>
                      {valor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          {/* `Select` sempre controlado desde o primeiro render (nunca
              `value={undefined}`) — string vazia é "nada selecionado" pro
              Radix Select; passar `undefined` no começo e um valor definido
              depois alterna o componente de uncontrolled pra controlled no
              meio do caminho, o que o Radix não suporta direito. */}
          <Field id="nova-novidade-categoria" label="Categoria" obrigatorio>
            {(props) => (
              <Select value={form.category} onValueChange={campo('category')}>
                <SelectTrigger id={props.id} aria-describedby={props['aria-describedby']}>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIAS).map(([valor, rotulo]) => (
                    <SelectItem key={valor} value={valor}>
                      {rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field id="nova-novidade-tipo" label="Tipo" obrigatorio>
            {(props) => (
              <Select value={form.type} onValueChange={campo('type')}>
                <SelectTrigger id={props.id} aria-describedby={props['aria-describedby']}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_NOVIDADE.map((valor) => (
                    <SelectItem key={valor} value={valor}>
                      {valor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>

        <Field id="nova-novidade-titulo" label="Título" obrigatorio>
          {(props) => <Input {...props} value={form.title} onChange={(e) => campo('title')(e.target.value)} />}
        </Field>

        <Field id="nova-novidade-resumo" label="Resumo" obrigatorio apoio="O que mudou, em 1–2 frases — é o texto do card no feed.">
          {(props) => (
            <textarea
              {...props}
              rows={4}
              value={form.summary}
              onChange={(e) => campo('summary')(e.target.value)}
              className={cn(inputClasses, 'h-auto resize-y py-2')}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          {/* `<input list>` + `<datalist>`, não `SearchSelect`: aqui precisa
              aceitar um nome NOVO que ainda não está na lista (qualquer um
              pode ser PO), não só escolher entre os existentes — o nativo do
              browser já faz isso de graça, sem componente novo. */}
          <Field id="nova-novidade-po" label="Product Owner" obrigatorio apoio="Escolha um PO já usado ou digite um novo.">
            {(props) => (
              <>
                <Input
                  {...props}
                  list="nova-novidade-po-sugestoes"
                  value={form.PO}
                  onChange={(e) => campo('PO')(e.target.value)}
                />
                <datalist id="nova-novidade-po-sugestoes">
                  {posExistentes.map((po) => (
                    <option key={po} value={po} />
                  ))}
                </datalist>
              </>
            )}
          </Field>
          <Field id="nova-novidade-link" label="Link do artigo" apoio="Opcional — sem link, o card mostra “artigo ainda não publicado”.">
            {(props) => (
              <Input
                {...props}
                type="url"
                value={form.link}
                onChange={(e) => campo('link')(e.target.value)}
                placeholder="https://nasajon.github.io/news/pan/..."
              />
            )}
          </Field>
        </div>

        {acao.erro ? <Callout tom="erro">{acao.erro.message}</Callout> : null}

        <DialogFooter>
          <Button variant="outline" onClick={fechar}>
            Cancelar
          </Button>
          <Button variant="primary" loading={acao.executando} disabled={!obrigatoriosPreenchidos} onClick={criar}>
            Publicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
